import { HttpErrorResponse } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';

import { createSelector, StoreConfig } from '@ngrx/store';
import {
  SeatKind,
  TELLENT_CURRENT_USER,
  TellentCurrentUser,
  TellentCurrentUserUpdate,
  TellentMember,
  TellentPasswordChange,
  TellentPasswordChangeResponse,
} from '@tellent/types';

import {
  createSingleEntityStore,
  memoizeSelector,
  ngrxEvent,
  pipeState,
  SingleEntityState,
} from '@recruitee/ngrx';

import { EntityStoreKey } from '../types';

export const TellentCurrentUserConfigToken = new InjectionToken('TellentCurrentUserConfigToken', {
  providedIn: 'root',
  factory: (): StoreConfig<SingleEntityState<TellentCurrentUser>> => ({
    initialState: {
      loaded: true,
      failed: false,
      pending: false,
      error: false,
      data: inject(TELLENT_CURRENT_USER),
    },
  }),
});

const { actions, selectors, reducer, namespace } = createSingleEntityStore<TellentCurrentUser>()(
  EntityStoreKey.TellentCurrentUser,
  {
    customActions: adapter => ({
      updatePassword: (state, payload: TellentPasswordChange) => adapter.markAsPending(state),
      updatePasswordSuccess: (state, payload: TellentPasswordChangeResponse) =>
        adapter.markAsLoaded(state),
      updatePasswordFail: ngrxEvent<HttpErrorResponse>(),

      updateCurrentUserData: (state, payload: Partial<TellentCurrentUserUpdate>) =>
        adapter.markAsPending(state),
      updateCurrentUserDataSuccess: (state, payload: TellentCurrentUser) =>
        pipeState(
          state,
          next => adapter.update(next, payload),
          next => adapter.markAsLoaded(next),
        ),

      updateCurrentUserDataFail: ngrxEvent<HttpErrorResponse>(),

      leaveCompany: (state, payload: { orgGuid: string }) => adapter.markAsPending(state),
      leaveCompanySuccess: (state, payload: TellentCurrentUser) =>
        pipeState(
          state,
          next => adapter.update(next, payload),
          next => adapter.markAsLoaded(next),
        ),
      leaveCompanyFail: ngrxEvent<HttpErrorResponse>(),
    }),
  },
);

const currentOrganizationMembershipSelector = createSelector(selectors.data, findCurrentMembership);
const seatByKindSelector = memoizeSelector((seatKind: SeatKind) =>
  createSelector(currentOrganizationMembershipSelector, function (membership: TellentMember) {
    return membership?.seats.find(seat => seat.app.kind === seatKind);
  }),
);

const isOrgOwner = createSelector(currentOrganizationMembershipSelector, member => member?.owner);

const TellentCurrentUserSelectors = {
  ...selectors,
  currentOrganizationMembershipSelector,
  seatByKindSelector,
  isOrgOwner,
};

export function findCurrentMembership(user: TellentCurrentUser): TellentMember {
  return user.members.find(member => member.orgGuid === user.currentOrgGuid)!;
}

export function fallbackCurrentMembership(user: TellentCurrentUser): TellentMember {
  return user.members.find(member => member.userGuid === user.guid)!;
}

export function isOrgGuidMatchingUserOrg(companyGuid: string, user: TellentCurrentUser) {
  if (!companyGuid) return false;
  return user.members.some(member => member.orgGuid === companyGuid);
}

export {
  actions as TellentCurrentUserEntityActions,
  TellentCurrentUserSelectors,
  reducer as TellentCurrentUserReducer,
  namespace as TellentCurrentUserNamespace,
};
